import {
  BloodPressureIcon,
  DownArrowIcon,
  RightArrowIcon,
  UpArrowIcon,
} from '@/Components/Common/Icons';
import { useDashboardModal } from '@/Components/Dashboard/DashboardModal';
import useUserEventsByTypesAndTimeRange from '@/Components/Hooks/useUserEventsByTypesAndTimeRange';

import { unitTitleData } from '@/Components/Wellness/WellnessUtils';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Card } from 'antd';
import { find, get, isEmpty } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import '../RecentActivity.less';
import {
  EmptyModalContent,
  ErrorModalContent,
  formatTime,
  getDateLabel,
  LoadingCard,
  LoadingModalContent,
  sortByLatestTimestamp,
} from '../RecentActivityUtils';

const ModalContent = ({ timezone, is24HourFormat, locale, data, isLoading, isError }) => {
  const trans = useSelector(translator);
  if (isLoading) return <LoadingModalContent />;

  if (isError) return <ErrorModalContent />;

  if (isEmpty(data)) return <EmptyModalContent />;

  const rows = sortByLatestTimestamp(data);
  return (
    <div className="recent-blood-pressure-table-card">
      <div className="recent-blood-pressure-table-head">
        <div className="head-cell head-cell-date">{trans('DATE_L')}</div>
        <div className="head-cell head-cell-bp">{trans('BLOODPRESSURE')}</div>
      </div>

      <div className="recent-blood-pressure-table-body">
        {rows.map((entry, index) => {
          const timestamp = get(entry, 'utc_timestamp_inms', '');
          const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
          const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
          const systolic = get(entry, 'systolic', '-');
          const diastolic = get(entry, 'diastolic', '-');
          const bloodPressureLabel = `${systolic}/${diastolic} mmHg`;

          return (
            <div
              className="recent-blood-pressure-table-row"
              key={`recent-blood-pressure-table-row-${index}`}
            >
              <div className="row-cell date-cell">
                <span className="date-text">{dateLabel}</span>
                <span className="time-text">{formattedTime}</span>
              </div>

              <div className="row-cell bp-cell">{bloodPressureLabel}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

ModalContent.propTypes = {
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
};

const BiometricBloodPressureCard = (props) => {
  const {
    useruuid,
    data,
    isLoading,
    timezone,
    is24HourFormat,
    locale,
    measurementSystem,
    // trendData,
  } = props;
  const trans = useSelector(translator);
  const { openDashboardModal } = useDashboardModal();
  const modalDataApi = useUserEventsByTypesAndTimeRange();
  const latestModalRequestIdRef = useRef(0);
  const invalidateModalRequest = useCallback(() => (latestModalRequestIdRef.current += 1), []);

  const getModalConfig = useCallback(
    (data = [], isLoading = true, isError = false, onClose = invalidateModalRequest) => ({
      title: (
        <div className="recent-activity-modal-title">
          <BloodPressureIcon
            className="recent-activity-modal-title-icon"
            hideEclipse={true}
            viewBox="15 15 30 30"
            fill="#005D98"
          />
          <span>{trans('BLOODPRESSURE')}</span>
        </div>
      ),
      content: <ModalContent {...props} data={data} isLoading={isLoading} isError={isError} />,
      onClose,
    }),
    [invalidateModalRequest, props, trans]
  );

  const fetchModalData = (requestId) => {
    const startDate = moment.utc().subtract(30, 'days').startOf('day').toISOString();
    const endDate = moment.utc().endOf('day').toISOString();
    const successCallback = (data) => {
      if (requestId !== latestModalRequestIdRef.current) {
        return;
      }

      openDashboardModal(getModalConfig(data, false));
    };
    const errorCallback = () => {
      if (requestId !== latestModalRequestIdRef.current) {
        return;
      }

      openDashboardModal(getModalConfig([], false, true));
    };
    modalDataApi.mutate({
      useruuid,
      startDateTimeUTC: startDate,
      endDateTimeUTC: endDate,
      eventTypes: [get(data, 'eventName')].filter(Boolean),
      successCallback,
      errorCallback,
    });
  };

  const handleCardClick = (e) => {
    e.preventDefault();
    const requestId = latestModalRequestIdRef.current + 1;
    latestModalRequestIdRef.current = requestId;
    openDashboardModal(getModalConfig());
    fetchModalData(requestId);
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const requestId = latestModalRequestIdRef.current + 1;
      latestModalRequestIdRef.current = requestId;
      openDashboardModal(getModalConfig());
      fetchModalData(requestId);
    }
  };

  if (isLoading) return <LoadingCard />;

  const timestamp = get(data, 'utc_timestamp_inms', '');
  const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
  const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
  const dataArray = get(data, 'data', []);
  const systolicDataKey = get(unitTitleData, [measurementSystem, 'systolic'], '');
  const diastolicDataKey = get(unitTitleData, [measurementSystem, 'diastolic'], '');
  const systolic = get(find(dataArray, ['display_label', systolicDataKey]), 'display_value', '');
  const diastolic = get(find(dataArray, ['display_label', diastolicDataKey]), 'display_value', '');
  const bloodPressureLabel = `${systolic}/${diastolic} mmHg`;

  // const trendPercent = get(trendData, 'trendPercent', null);
  // const isUp = trendPercent > 0;
  // const isDown = trendPercent < 0;
  // const displayPercent = trendPercent !== null ? Math.abs(trendPercent) : null;

  const CardTitle = (
    <div className="recent-activity-card-header">
      <BloodPressureIcon
        className="recent-activity-card-icon"
        hideEclipse={true}
        viewBox="15 15 30 30"
        fill="#005D98"
      />
      <h4 className="recent-activity-card-title">{trans('BLOODPRESSURE')}</h4>
    </div>
  );

  return (
    <Card
      className="recent-activity-card"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      hoverable
      variant="borderless"
      title={CardTitle}
    >
      <div className="recent-activity-card-body">
        <p className="recent-activity-time">
          {dateLabel} {formattedTime}
        </p>
        <p className="recent-activity-data-value">{bloodPressureLabel}</p>
      </div>
      {/* <>
        <hr className="recent-activity-divider" />
        <div className="recent-activity-footer-section">
          <p className="recent-activity-footer-label">{trans('READINGS_TAKEN')}</p>
          <div className="recent-activity-card-footer">
            <span className="recent-activity-comparison">
              {trans('YESTERDAY_VS_LAST_WEEK_AVG')}
            </span>
            {displayPercent !== null ? (
              <span
                className={`recent-activity-trend ${isUp ? 'up' : isDown ? 'down' : 'neutral'}`}
              >
                {isUp ? (
                  <UpArrowIcon className="trend-arrow" />
                ) : isDown ? (
                  <DownArrowIcon className="trend-arrow" />
                ) : (
                  <RightArrowIcon className="trend-arrow" />
                )}
                <span>{displayPercent}%</span>
              </span>
            ) : (
              <span className="recent-activity-trend neutral">-</span>
            )}
          </div>
        </div>
      </> */}
    </Card>
  );
};

BiometricBloodPressureCard.propTypes = {
  useruuid: PropTypes.string,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  measurementSystem: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  isLoading: PropTypes.bool,
  trendData: PropTypes.object,
};

export default BiometricBloodPressureCard;
